'use client'

import { useMemo } from 'react'
import { Laptop, Smartphone, X } from 'lucide-react'
import type { CompanySessionDTO, SessionDTO } from '@rr/api'
import {
  Avatar,
  EntityListShell,
  Spinner,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@rr/ui'
import {
  useCompanySessions,
  useRevokeCompanySession,
} from '@/modules/platform/hooks/usePlatformCompanies'

/** "3 minutes ago" via platform Intl — no date dependency. */
function relativeTime(iso: string): string {
  const diffSec = Math.round((new Date(iso).getTime() - Date.now()) / 1000)
  const rtf = new Intl.RelativeTimeFormat(undefined, { numeric: 'auto' })
  const divisions: Array<[number, Intl.RelativeTimeFormatUnit]> = [
    [60, 'second'],
    [60, 'minute'],
    [24, 'hour'],
    [7, 'day'],
    [4.34524, 'week'],
    [12, 'month'],
    [Number.POSITIVE_INFINITY, 'year'],
  ]
  let value = diffSec
  for (const [amount, unit] of divisions) {
    if (Math.abs(value) < amount) return rtf.format(Math.round(value), unit)
    value /= amount
  }
  return rtf.format(Math.round(value), 'year')
}

function SessionCell({
  session,
  Icon,
  onRevoke,
  revoking,
}: {
  session: SessionDTO | null
  Icon: typeof Laptop
  onRevoke: (id: string) => void
  revoking: boolean
}) {
  if (!session) return <span className="text-muted-foreground">—</span>
  return (
    <div className="flex items-center gap-2">
      <Icon className="h-4 w-4 shrink-0 text-muted-foreground" />
      <div className="min-w-0">
        <p className="truncate text-sm font-medium">{session.label}</p>
        <p className="truncate text-xs text-muted-foreground">
          {session.ipAddress ? `${session.ipAddress} · ` : ''}
          {relativeTime(session.lastSeenAt)}
        </p>
      </div>
      <button
        type="button"
        aria-label="Revoke session"
        disabled={revoking}
        onClick={() => onRevoke(session.id)}
        className="shrink-0 rounded p-1 text-muted-foreground hover:text-destructive disabled:opacity-50"
      >
        <X className="h-3.5 w-3.5" />
      </button>
    </div>
  )
}

type Grouped = {
  userId: string
  name: string
  email: string
  web: SessionDTO | null
  mobile: SessionDTO | null
}

export default function CompanySessionsList({ companyId }: { companyId: string }) {
  const { data: sessions, isLoading, isError, error } = useCompanySessions(companyId)
  const revoke = useRevokeCompanySession(companyId)

  // Group the flat session list into one row per user → Web / Mobile.
  // The cap (one non-revoked session per clientType) means at most one
  // of each per user.
  const rows = useMemo<Grouped[]>(() => {
    const map = new Map<string, Grouped>()
    for (const s of (sessions ?? []) as CompanySessionDTO[]) {
      const entry =
        map.get(s.user.id) ??
        ({
          userId: s.user.id,
          name: s.user.name,
          email: s.user.email,
          web: null,
          mobile: null,
        } satisfies Grouped)
      if (s.clientType === 'mobile') entry.mobile = s
      else entry.web = s
      map.set(s.user.id, entry)
    }
    return [...map.values()]
  }, [sessions])

  return (
    <EntityListShell
      empty={!isLoading && rows.length === 0}
      emptyState={
        isError
          ? error instanceof Error
            ? error.message
            : 'Failed to load sessions.'
          : 'No active sessions in this organization.'
      }
    >
      {isLoading ? (
        <div className="flex items-center justify-center py-16">
          <Spinner />
        </div>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>User</TableHead>
              <TableHead>Web session</TableHead>
              <TableHead>Mobile session</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((r) => (
              <TableRow key={r.userId}>
                <TableCell>
                  <div className="flex items-center gap-3 min-w-0">
                    <Avatar name={r.name || r.email} size={32} />
                    <div className="min-w-0">
                      <p className="font-medium text-foreground truncate">{r.name || '—'}</p>
                      <p className="text-xs text-muted-foreground truncate">{r.email}</p>
                    </div>
                  </div>
                </TableCell>
                <TableCell>
                  <SessionCell
                    session={r.web}
                    Icon={Laptop}
                    onRevoke={revoke.mutate}
                    revoking={revoke.isPending && revoke.variables === r.web?.id}
                  />
                </TableCell>
                <TableCell>
                  <SessionCell
                    session={r.mobile}
                    Icon={Smartphone}
                    onRevoke={revoke.mutate}
                    revoking={revoke.isPending && revoke.variables === r.mobile?.id}
                  />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </EntityListShell>
  )
}
